import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProviderCard, RepoCard, ImportDialog, EmptyState } from "../../components/dashboard";
import { getConnectedProviders, listRepos } from "../../lib/api/cloud";
import type { GitProvider, GitRepo, ImportResult } from "../../types/cloud";

type PageState = "providers" | "repos";

function RepoSkeleton() {
  return (
    <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 animate-pulse">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="h-4 bg-[#333] rounded w-40 mb-2" />
          <div className="h-3 bg-[#333] rounded w-64 mb-2" />
          <div className="flex gap-3">
            <div className="h-3 bg-[#333] rounded w-20" />
            <div className="h-3 bg-[#333] rounded w-16" />
            <div className="h-3 bg-[#333] rounded w-24" />
          </div>
        </div>
        <div className="h-7 bg-[#333] rounded w-16" />
      </div>
    </div>
  );
}

export function ImportProject() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Provider selection state
  const [providers, setProviders] = useState<GitProvider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [providersError, setProvidersError] = useState<string | null>(null);

  // Repo browser state
  const [pageState, setPageState] = useState<PageState>("providers");
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [repos, setRepos] = useState<GitRepo[]>([]);
  const [reposLoading, setReposLoading] = useState(false);
  const [reposError, setReposError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Import dialog state
  const [importRepo, setImportRepo] = useState<GitRepo | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Debounce ref
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch providers
  useEffect(() => {
    async function fetchProviders() {
      try {
        const data = await getConnectedProviders();
        setProviders(data.providers);

        // Auto-select if only one provider is connected
        const connected = data.providers.filter((p) => p.connected);
        if (connected.length === 1) {
          setSelectedProvider(connected[0].name);
          setPageState("repos");
        }
      } catch (err) {
        setProvidersError(err instanceof Error ? err.message : t('dashboard.importProject.failedToLoadProviders'));
      } finally {
        setProvidersLoading(false);
      }
    }
    fetchProviders();
  }, [t]);

  // Fetch repos when provider changes or search changes
  const fetchRepos = useCallback(
    async (provider: string, searchQuery: string, pageNum: number, append: boolean) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setReposLoading(true);
        setReposError(null);
      }

      try {
        const data = await listRepos(provider, {
          page: pageNum,
          search: searchQuery || undefined,
        });
        if (append) {
          setRepos((prev) => [...prev, ...data.repos]);
        } else {
          setRepos(data.repos);
        }
        setHasMore(data.hasMore);
        setPage(pageNum);
      } catch (err) {
        setReposError(err instanceof Error ? err.message : t('dashboard.importProject.failedToLoadRepos'));
      } finally {
        setReposLoading(false);
        setLoadingMore(false);
      }
    },
    [t]
  );

  // Fetch repos on provider select
  useEffect(() => {
    if (pageState === "repos" && selectedProvider) {
      fetchRepos(selectedProvider, "", 1, false);
    }
  }, [pageState, selectedProvider, fetchRepos]);

  // Debounced search
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (selectedProvider) {
        fetchRepos(selectedProvider, value, 1, false);
      }
    }, 300);
  };

  const handleLoadMore = () => {
    if (selectedProvider && hasMore && !loadingMore) {
      fetchRepos(selectedProvider, search, page + 1, true);
    }
  };

  const handleProviderSelect = (providerName: string) => {
    setSelectedProvider(providerName);
    setSearch("");
    setRepos([]);
    setPage(1);
    setPageState("repos");
  };

  const handleBack = () => {
    setPageState("providers");
    setSelectedProvider(null);
    setSearch("");
    setRepos([]);
    setReposError(null);
  };

  const handleImportClick = (repo: GitRepo) => {
    setImportRepo(repo);
    setImportDialogOpen(true);
  };

  const handleImportSuccess = (_result: ImportResult) => {
    // Dialog shows success state; user can close or open CloseCode ADE
  };

  const handleImportClose = () => {
    setImportDialogOpen(false);
    setImportRepo(null);
  };

  const selectedProviderInfo = providers.find((p) => p.name === selectedProvider);

  // Loading state
  if (providersLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // Error state
  if (providersError) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link
            to="/dashboard"
            className="text-[#999] hover:text-[#f5f5f5] text-sm transition-colors"
          >
            &larr; {t('dashboard.importProject.backToDashboard')}
          </Link>
        </div>
        <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-8">
          <EmptyState
            title={t('dashboard.importProject.failedToLoadProviders')}
            description={providersError}
            action={
              <button
                onClick={() => navigate(0)}
                className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/20 transition-colors"
              >
                {t('dashboard.instance.retry')}
              </button>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        {pageState === "providers" ? (
          <Link
            to="/dashboard"
            className="text-[#999] hover:text-[#f5f5f5] text-sm transition-colors"
          >
            &larr; {t('dashboard.importProject.backToDashboard')}
          </Link>
        ) : (
          <button
            onClick={handleBack}
            className="text-[#999] hover:text-[#f5f5f5] text-sm transition-colors"
          >
            &larr; {t('dashboard.importProject.backToProviders')}
          </button>
        )}
        <h1 className="text-2xl font-bold text-[#f5f5f5] mt-2">
          {pageState === "repos" && selectedProviderInfo
            ? t('dashboard.importProject.importFrom', { provider: selectedProviderInfo.displayName })
            : t('dashboard.importProject.title')}
        </h1>
        <p className="text-[#999] text-sm mt-1">
          {pageState === "repos"
            ? t('dashboard.importProject.selectRepo')
            : t('dashboard.importProject.chooseProvider')}
        </p>
      </div>

      {/* Provider Selection */}
      {pageState === "providers" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {providers.map((provider) => (
            <ProviderCard
              key={provider.name}
              provider={provider}
              onClick={() => handleProviderSelect(provider.name)}
            />
          ))}
        </div>
      )}

      {/* Repo Browser */}
      {pageState === "repos" && (
        <div>
          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('dashboard.importProject.searchPlaceholder')}
              className="w-full px-4 py-2.5 bg-[#1a1a1a] border border-[#333] rounded-lg text-[#f5f5f5] text-sm placeholder-[#666] focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
            />
          </div>

          {/* Error */}
          {reposError && (
            <div className="mb-4 p-3 bg-red-500/5 border border-red-500/20 rounded text-red-400 text-sm">
              {reposError}
            </div>
          )}

          {/* Loading skeletons */}
          {reposLoading && (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <RepoSkeleton key={i} />
              ))}
            </div>
          )}

          {/* Repo list */}
          {!reposLoading && repos.length > 0 && (
            <div className="space-y-3">
              {repos.map((repo) => (
                <RepoCard key={repo.id} repo={repo} onImport={handleImportClick} />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!reposLoading && repos.length === 0 && !reposError && (
            <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-8">
              <EmptyState
                title={t('dashboard.importProject.noRepos')}
                description={
                  search
                    ? t('dashboard.importProject.noReposSearch', { search })
                    : t('dashboard.importProject.noReposProvider')
                }
              />
            </div>
          )}

          {/* Load More */}
          {!reposLoading && hasMore && (
            <div className="mt-4 flex justify-center">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="px-6 py-2 bg-[#1a1a1a] border border-[#333] rounded-lg text-[#999] text-sm font-medium hover:border-[#555] hover:text-[#f5f5f5] transition-colors disabled:opacity-40 inline-flex items-center gap-2"
              >
                {loadingMore && (
                  <div className="w-3.5 h-3.5 border-2 border-[#666] border-t-[#f5f5f5] rounded-full animate-spin" />
                )}
                {loadingMore ? t('dashboard.importProject.loading') : t('dashboard.importProject.loadMore')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Import Dialog */}
      <ImportDialog
        open={importDialogOpen}
        repo={importRepo}
        onClose={handleImportClose}
        onSuccess={handleImportSuccess}
      />
    </div>
  );
}
